import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import classNames from "classnames";
import Geohash from "latlon-geohash";
import { Map as YandexMap } from "yandex-maps";
import { useWindowSize } from "@src/hooks/useWindowsSize";
import { useGetOffersQuery } from "@app/store/offersApi";
import { AppState } from "@app/store/store";
import { ListIcon } from "@shared/ui/Icon/ui/Common/ListIcon";
import { LocationIcon } from "@shared/ui/Icon/ui/Common/LocationIcon";
import { YandexMapLoader } from "@shared/ui/YandexMapLoader";
import { createOffersLabel, numberWithSpaces } from "../../../../utils/utils";
import MapViewCardsList from "../map-view-cards-list/map-view-cards-list";
import { DEFAULT_LOCATION, DEFAULT_ZOOM } from "./constants";
import { MapViewProps } from "./types";
import classes from "./map-view.module.scss";

const DynamicLocationFilter = dynamic(
    () => import("@components/Features/MainPage/LocationFilter/LocationFilter"),
    {
        ssr: false
    }
);

let myMap: YandexMap;
let isMapInitiated = false;
const MapView = ({ setViewMode, total, data, onBoundsChanged, filters }: MapViewProps) => {
    const size = useWindowSize();
    const [isMobile, setIsMobile] = useState(false);

    const router = useRouter();
    const pathname = usePathname();
    const me = useRef<HTMLInputElement>(null);

    const checkIfMapInitiated = () => {
        const meCurrent = me.current;
        if (!meCurrent) {
            return false;
        }
        const mapExists = meCurrent.querySelector("#YMapsID ymaps");
        return !!mapExists;
    };

    const [selectedAdsIds, _setSelectedAdsIds] = useState<number[]>([]);
    const [isCardsContainerOpened, setIsCardsContainerOpened] = useState(false);
    const [isLoading, _setIsLoading] = useState(false);
    const [isLocationFilterShown, setIsLocationFilterShown] = useState(false);

    const [currentMap, setCurrentMap] = useState<YandexMap | null>(null);
    const savedCities = useSelector((state: AppState) => state.filters.city);
    const mapCenter = useSelector((state: AppState) => state.filters.cityCenterCoords);
    const mapLoaded = useSelector((state: AppState) => state.layout.mapLoaded);
    const city = savedCities[0]?.name;

    const [currentGeoHash, setCurrentGeoHash] = useState<string>("");
    const [currentPage, setCurrentPage] = useState(1);
    const { data: currentOffersData, isFetching } = useGetOffersQuery(
        {
            geo_hash: currentGeoHash,
            limit: 20,
            ...(currentGeoHash ? { page: currentPage } : {}),
            ...filters
        },
        {
            skip: currentGeoHash === ""
        }
    );

    const closeAdsBlock = () => {
        setIsCardsContainerOpened(false);
    };
    const handleSwitcherClick = () => {
        router.push(pathname ? pathname : "/catalog");
        setViewMode(false);
    };
    const handleLocationClose = () => {
        setIsLocationFilterShown(false);
    };

    useEffect(() => {
        if (myMap) {
            const ymaps = window.ymaps;
            if (!ymaps) {
                return;
            }
            ymaps.ready(function () {
                myMap.geoObjects.removeAll();

                const getDiameter = (qnt: number) => {
                    if (qnt < 10) {
                        return 50;
                    } else if (qnt < 100) {
                        return 60;
                    } else if (qnt < 1000) {
                        return 70;
                    } else if (qnt < 10000) {
                        return 80;
                    } else if (qnt < 100000) {
                        return 90;
                    } else {
                        return 100;
                    }
                };

                if (data) {
                    for (let i = 0; i < data.length; i++) {
                        const { doc_count: qnt, key: geoHash } = data[i];

                        const coords = Geohash.decode(geoHash);

                        let MyIconLayout = ymaps.templateLayoutFactory.createClass(
                            `<div id="map-point-offer-${geoHash}"><svg class="my-custom-cluster-point"  height="$[properties.diameter]" width="$[properties.diameter]">
            <circle cx="$[properties.radius]" cy="$[properties.radius]" r="$[properties.radius2]" stroke="white" stroke-width="3" fill="white" />
            <circle cx="$[properties.radius]" cy="$[properties.radius]" r="$[properties.radius3]" stroke="#3b85f7" stroke-width="4" fill="white" />
           <text x="50%" y="52%" text-anchor="middle" dy=".3em">$[properties.quantity]</text>
            </g>
          </svg></div>`
                        );

                        let myPlacemark = new ymaps.Placemark(
                            [coords.lat, coords.lon],
                            {
                                quantity: qnt,
                                diameter: getDiameter(qnt),
                                radius: getDiameter(qnt) / 2,
                                radius2: getDiameter(qnt) / 2 - 2,
                                radius3: getDiameter(qnt) / 2 - 5,
                                geoHash
                            },
                            {
                                iconLayout: MyIconLayout,
                                hintContent: "22600",

                                iconShape: {
                                    type: "Circle",
                                    coordinates: [getDiameter(qnt) / 2, getDiameter(qnt) / 2],
                                    radius: getDiameter(qnt) / 2
                                }
                            }
                        );

                        myPlacemark.events.add("click", function (e: any) {
                            let target = e.get("target");
                            const qnt = target?.properties?.get("quantity");
                            let zoom = myMap.getZoom();

                            if (qnt <= 10 || zoom > 15) {
                                const geoHash = target?.properties?.get("geoHash");
                                if (!geoHash) {
                                    throw new Error("geoHash is not defined");
                                }

                                setCurrentGeoHash(geoHash);
                                setIsCardsContainerOpened(true);
                            } else {
                                let coords = target.geometry.getCoordinates();

                                myMap.setCenter(coords, zoom + 1, {
                                    duration: 300
                                });
                            }
                        });

                        myMap.geoObjects.add(myPlacemark);
                    }
                }

                onBoundsChanged(myMap.getBounds(), myMap.getZoom());
            });
        }
    }, [data, isMapInitiated]);

    useEffect(() => {
        if (size.width) {
            if (size.width < 600) {
                setIsMobile(true);
            } else {
                setIsMobile(false);
            }
        }
    }, [size]);

    const mapInit = () => {
        const ymaps = window.ymaps;
        if (!ymaps) {
            return;
        }
        ymaps.ready(function () {
            if (isMapInitiated) {
                return;
            }

            myMap = new ymaps.Map(
                "YMapsID",
                {
                    center: mapCenter || [55.751574, 37.573856],
                    zoom: DEFAULT_ZOOM,

                    controls: ["zoomControl"],
                    behaviors: ["default", "scrollZoom"]
                },
                {
                    minZoom: 5,
                    maxZoom: 16
                }
            );

            setCurrentMap(myMap);

            myMap.events.add("boundschange", function (e: any) {
                let newBounds = e.get("newBounds");
                let zoom = e.get("newZoom");
                onBoundsChanged(newBounds, zoom);
            });

            isMapInitiated = true;
        });
    };

    useEffect(() => {
        mapInit();
    }, [mapLoaded]);

    useEffect(() => {
        const ymaps = window.ymaps;
        if (!ymaps) {
            return;
        }
        ymaps.ready(function () {
            if (!myMap || !currentMap) return;
            if (mapCenter && mapCenter.length === 2 && mapCenter[0] && mapCenter[1]) {
                myMap.setCenter([mapCenter[0], mapCenter[1]]);
                currentMap.setCenter([mapCenter[0], mapCenter[1]]);
            }
        });
    }, [mapCenter, myMap, currentMap]);

    useEffect(() => {
        const isInitiated = checkIfMapInitiated();
        if (!isInitiated) {
            isMapInitiated = false;
            mapInit();
        }
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [currentGeoHash]);

    return (
        <div ref={me} className={classes["map-view"]}>
            {isCardsContainerOpened && (
                <div className={classes["map-view__cards"]}>
                    <MapViewCardsList
                        isContentLoading={isFetching}
                        selectedAdsIds={selectedAdsIds}
                        closeAdsBlock={closeAdsBlock}
                        currentOffers={currentOffersData?.offers || []}
                        currentPage={currentPage}
                        metaTotal={currentOffersData?.meta?.total || null}
                        onLoadMore={() => {
                            setCurrentPage((prev) => prev + 1);
                        }}
                    />
                </div>
            )}
            <div
                className={classNames(classes["map-view__switcher"], {
                    [classes["map-view__switcher-mobile"]]: isMobile,
                    [classes["map-view__switcher-desktop"]]: !isMobile
                })}
                onClick={handleSwitcherClick}
            >
                <ListIcon className={classes["map-view__switcher-icon"]} />
                {total && (
                    <span className={classes["map-view__switcher-text"]}>
                        {numberWithSpaces(total)} {createOffersLabel(total)} списком
                    </span>
                )}
            </div>
            <div
                className={classNames(classes["map-view__radius"], {
                    [classes["map-view__radius-mobile"]]: isMobile,
                    [classes["map-view__radius-opened"]]: !isMobile && isCardsContainerOpened,
                    [classes["map-view__radius-desktop"]]: !isMobile && !isCardsContainerOpened
                })}
                id="catalog-map-switcher"
                onClick={() => {
                    setIsLocationFilterShown(!isLocationFilterShown);
                }}
            >
                <LocationIcon className={classes["map-view__radius-icon"]} color="white" />
                {city || DEFAULT_LOCATION}
            </div>

            {isLocationFilterShown && (
                <div
                    className={classNames(classes["map-view__location"], {
                        [classes["map-view__location-mobile"]]: isMobile,
                        [classes["map-view__location-opened"]]: !isMobile && isCardsContainerOpened,
                        [classes["map-view__location-desktop"]]:
                            !isMobile && !isCardsContainerOpened
                    })}
                >
                    <DynamicLocationFilter isMultiple={false} onClose={handleLocationClose} />
                </div>
            )}
            <div
                className={classNames(classes["map-view__container"], {
                    [classes["map-view__container-loading"]]: isLoading
                })}
                id="YMapsID"
            ></div>
            <YandexMapLoader />
        </div>
    );
};
export default MapView;